<script setup lang="ts">
/**
 * Единственный `<dialog>` проекта. Порядок окон ведёт top layer браузера: окно, открытое позже, всегда выше.
 *
 * Слот: один корневой элемент, он же цель анимации. `transform` и `filter` на самом `dialog` запрещены,
 * они сломают `fixed` у попапов, которые `OverlayTeleport` кладёт сюда же.
 * Попап со своим Esc обязан вызвать `preventDefault()`, иначе браузер закроет окно.
 */
import type { TransitionProps } from 'vue';

import { useEventListener } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, provide, ref, useId, useTemplateRef, watch } from 'vue';

import { isTopDialog, pushDialog, removeDialog } from '@/shared/lib/helpers/open-dialogs';

import { OVERLAY_HOST } from './overlay-host';
import { OPACITY_FADE_TRANSITION } from './transitions';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    open: boolean;
    closeOnEscape?: boolean;
    backdropClass?: string;
    transition?: TransitionProps;
  }>(),
  { closeOnEscape: true, backdropClass: undefined, transition: undefined },
);

const emit = defineEmits<{
  'request-close': [];
  'backdrop-click': [];
}>();

const dialog = useTemplateRef<HTMLDialogElement>('dialog');
const hostId = useId();
provide(
  OVERLAY_HOST,
  computed(() => (dialog.value ? `#${hostId}` : null)),
);

// Окно живёт до конца анимации ухода, поэтому `leaving` меняем в той же отрисовке, что и `open`.
const leaving = ref(false);

watch(
  () => props.open,
  (open) => {
    leaving.value = !open;
  },
);

const showDialog = (): void => {
  const element = dialog.value;
  if (!element || element.open) return;

  element.showModal();
  pushDialog(element);
  element.querySelector<HTMLElement>('[data-autofocus]')?.focus();
};

const releaseDialog = (): void => {
  const element = dialog.value;
  if (!element) return;

  removeDialog(element);
  element.close();
};

onMounted(showDialog);
watch(
  () => props.open,
  (open) => {
    if (open) showDialog();
  },
  { flush: 'post' },
);

const handleAfterLeave = (): void => {
  // Повторное открытие: Vue досрочно завершает прежний уход этим же событием, окно остаётся открытым.
  if (props.open) return;

  releaseDialog();
  leaving.value = false;
};

// Гости уезжают до удаления DOM, а close() возвращает фокус на элемент, откуда открыли окно.
onBeforeUnmount(releaseDialog);

// Фаза всплытия на window: слушатели попапов на document уже отработали и могли забрать Esc себе.
useEventListener(
  () => (dialog.value ? window : null),
  'keydown',
  (event: KeyboardEvent) => {
    const element = dialog.value;
    if (event.key !== 'Escape' || event.defaultPrevented || !element || !isTopDialog(element)) return;

    event.preventDefault();
    if (props.open && props.closeOnEscape) emit('request-close');
  },
);

const handleCancel = (event: Event): void => {
  event.preventDefault();
  emit('request-close');
};

// Браузер может закрыть окно, не дав отменить (Android «Назад» без жеста на странице). Решает владелец окна.
// Своё закрытие отличаем по реестру: releaseDialog убирает окно из него до close().
const handleNativeClose = (): void => {
  const element = dialog.value;
  if (!element || !props.open || element.open || !isTopDialog(element)) return;

  console.warn('[OverlayDialog.native-close]');
  element.showModal();
  emit('request-close');
};
</script>

<template>
  <Teleport to="#app">
    <dialog
      v-if="open || leaving"
      :id="hostId"
      ref="dialog"
      v-bind="$attrs"
      autofocus
      data-pull-to-refresh-ignore
      class="fixed inset-0 m-0 size-full max-h-none max-w-none overflow-visible border-0 bg-transparent p-0 text-inherit outline-none backdrop:bg-transparent"
      @cancel.self="handleCancel"
      @close="handleNativeClose"
    >
      <Transition
        appear
        v-bind="OPACITY_FADE_TRANSITION"
      >
        <div
          v-if="open && backdropClass"
          class="fixed inset-0"
          :class="backdropClass"
          @click="emit('backdrop-click')"
        />
      </Transition>

      <Transition
        appear
        v-bind="transition"
        @after-leave="handleAfterLeave"
      >
        <slot v-if="open" />
      </Transition>
    </dialog>
  </Teleport>
</template>
